import React, { useMemo, useState } from "react";
import { Row, Col, Table, Badge, Button, Alert } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
  markERAClaimAllocated,
  markERAClaimDismissed,
} from "../../afinanceapi";
import CreateReceiptModal from "../Accounts/Receipt.jsx";
import {
  getAccountByPatientId,
  getPatientIdByMedAidDepCode,
  checkAccountByMedicalAid,
} from "../../api.js";

const STATUS_META = {
  allocated: { label: "Allocated", bg: "success", icon: "fa-circle-check" },
  unallocated: { label: "Unallocated", bg: "danger", icon: "fa-circle-exclamation" },
  skipped_finalised: {
    label: "Already settled",
    bg: "secondary",
    icon: "fa-circle-info",
  },
  duplicate_in_bundle: {
    label: "Duplicate",
    bg: "warning",
    icon: "fa-clone",
  },
  reversal: {
    label: "Reversal / Clawback",
    bg: "danger",
    icon: "fa-rotate-left",
  },
  dismissed: { label: "Dismissed", bg: "dark", icon: "fa-eye-slash" },
};

function statusMeta(status) {
  return STATUS_META[status] || { label: status || "—", bg: "light", icon: "fa-question" };
}

// dateGroup.date is normalised to "YYYYMMDD" server-side (recordDateGroupOutcome
// → eraDateToYYYYMMDD in ERA.js). `new Date("20260506")` returns Invalid Date
// because the constructor only parses ISO 8601 strings with separators. Insert
// the hyphens so toLocaleDateString() works. Pass-through for already-ISO input
// keeps the helper safe for any future callers.
function formatGroupDate(raw) {
  if (!raw) return "—";
  const s = String(raw);
  const iso = /^\d{8}$/.test(s)
    ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`
    : s;
  const d = new Date(iso);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
}

function fmtZAR(x) {
  if (x === null || x === undefined || x === "") return "";
  return new Intl.NumberFormat("en-ZA", {
    style: "currency",
    currency: "ZAR",
    minimumFractionDigits: 2,
  }).format(Number(x) || 0);
}

/**
 * Renders one original <era:Claim> in full: header, totals, all lines and
 * every dateGroup outcome (allocated / unallocated / already-settled /
 * duplicate). The line table shows every line GEMS bundled under this
 * patient, with each line stamped with the status of its date group so the
 * reader can see at a glance which money landed where.
 */
export default function EraClaimCard({ eraClaim, eraLogId, onMutated }) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState(null); // dateGroupDate currently mutating
  const [error, setError] = useState("");
  const [receiptModalShow, setReceiptModalShow] = useState(false);
  const [allocCtx, setAllocCtx] = useState({ patient: null, account: null });

  // line.key → dateGroup mapping so each row gets the right status badge.
  // A line not covered by any group is unusual but we render it as "—" so
  // the row still shows up rather than vanishing.
  const lineStatusByKey = useMemo(() => {
    const map = new Map();
    for (const g of eraClaim?.dateGroups || []) {
      for (const k of g?.lineKeys || []) {
        map.set(String(k), g);
      }
    }
    return map;
  }, [eraClaim]);

  const e = eraClaim?.eraClaim || {};
  const totals = eraClaim?.totals || {};
  const lines = Array.isArray(eraClaim?.lines) ? eraClaim.lines : [];
  const dateGroups = Array.isArray(eraClaim?.dateGroups) ? eraClaim.dateGroups : [];
  const hasReversal = dateGroups.some((g) => g?.status === "reversal");

  const handleAllocate = async (group) => {
    setError("");
    try {
      // Resolve account by medical-aid + dep code (same flow as old
      // UnallocatedERA). Once resolved, the receipt modal handles posting;
      // when it closes we mark the dateGroup allocated server-side.
      const patientId = await getPatientIdByMedAidDepCode(
        e.accountMedicalAidNumber,
        e.dependantCode
      );
      if (!patientId) {
        alert("No patient found for the specified medical aid and dependant code.");
        return;
      }
      const account = await getAccountByPatientId(patientId);
      if (!account) {
        alert("No account found for the selected patient.");
        return;
      }
      setAllocCtx({
        patient: patientId,
        account,
        schemeClaimId: e.schemeClaimId,
        dateGroupDate: group.date,
        treatmentDate: e.treatmentDate,
      });
      setReceiptModalShow(true);
    } catch (err) {
      console.error("Allocate error:", err);
      setError(err?.message || "Failed to start allocation.");
    }
  };

  const handleReceiptClosed = async () => {
    // Receipt either succeeded or was cancelled. Flag the dateGroup as
    // allocated on the server either way — the user has actioned it and the
    // entry should leave the unallocated list. Refresh from parent.
    setReceiptModalShow(false);
    if (!allocCtx?.schemeClaimId) return;
    setBusy(allocCtx.dateGroupDate);
    try {
      await markERAClaimAllocated({
        eraLogId,
        schemeClaimId: allocCtx.schemeClaimId,
        treatmentDate: allocCtx.treatmentDate,
        dateGroupDate: allocCtx.dateGroupDate,
      });
      if (onMutated) await onMutated();
    } catch (err) {
      console.error("Mark-allocated error:", err);
      setError(err?.message || "Failed to mark as allocated.");
    } finally {
      setBusy(null);
      setAllocCtx({ patient: null, account: null });
    }
  };

  const handleDismiss = async (group) => {
    if (!window.confirm(`Dismiss the ${group.date} group? This removes it from your unallocated list.`)) return;
    setError("");
    setBusy(group.date);
    try {
      await markERAClaimDismissed({
        eraLogId,
        schemeClaimId: e.schemeClaimId,
        treatmentDate: e.treatmentDate,
        dateGroupDate: group.date,
      });
      if (onMutated) await onMutated();
    } catch (err) {
      console.error("Dismiss error:", err);
      setError(err?.message || "Failed to dismiss.");
    } finally {
      setBusy(null);
    }
  };

  const handleGoToAccount = async () => {
    try {
      const acct = await checkAccountByMedicalAid(e.accountMedicalAidNumber);
      if (acct?._id) {
        navigate(`/accounts/${acct._id}`);
      } else {
        alert("No account found for the specified medical aid number.");
      }
    } catch (err) {
      console.error("Go-to-account error:", err);
      alert("An error occurred while navigating to the account.");
    }
  };

  return (
    <div
      className="p-2 mt-1 bg-white border-bottom border-secondary rounded"
      style={{ fontSize: "0.9rem" }}
    >
      {/* Header row — patient, member, account ref, totals */}
      <Row className="mb-1 mt-1">
        <Col md={3}>
          <div className="fw-semibold text-primary">
            {e.patientName || `${e.patientFirstName || ""} ${e.patientLastName || ""}`.trim() || "(unknown patient)"}
          </div>
          <div className="small">
            Medical Aid #:{" "}
            <span className="fw-semibold">
              {e.accountMedicalAidNumber || "-"}
            </span>
            {e.dependantCode && (
              <span className="fw-semibold"> ({e.dependantCode})</span>
            )}
          </div>
          <div className="small">
            Account Ref:{" "}
            <span className="fw-semibold">{e.accountReference || "-"}</span>
          </div>
        </Col>
        <Col md={3}>
          <div className="fw-semibold text-primary">
            {e.claimPracticeName} ({e.claimPracticeNumber})
          </div>
          <div className="small">
            Treatment Date:{" "}
            {e.treatmentDate ? new Date(e.treatmentDate).toLocaleDateString() : "-"}
          </div>
          <div className="small">
            Scheme Claim #:{" "}
            <span className="text-muted">{e.schemeClaimId || "-"}</span>
          </div>
        </Col>
        <Col md={4}>
          <div className="small">
            <Row className="mb-1">
              <Col xs={6} className="text-primary fw-semibold text-end">
                Claimed (Header):
              </Col>
              <Col xs={4} className="text-end">
                {fmtZAR(totals.claimedByProvider || 0)}
              </Col>
            </Row>
            <Row className="mb-1">
              <Col xs={6} className="text-primary fw-semibold text-end">
                Paid to Provider:
              </Col>
              <Col xs={4} className="text-end">
                {fmtZAR(totals.paidToProvider || 0)}
              </Col>
            </Row>
            <Row>
              <Col xs={6} className="text-primary fw-semibold text-end">
                Member To Pay:
              </Col>
              <Col xs={4} className="text-end">
                {fmtZAR(totals.memberToPayProvider || 0)}
              </Col>
            </Row>
          </div>
        </Col>
        <Col md={2} className="d-flex flex-column align-items-end gap-1">
          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleGoToAccount}
            className="fw-semibold w-100"
          >
            <i className="fa-solid fa-user me-2" />
            Account
          </Button>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" className="py-1 my-2 small">
          {error}
        </Alert>
      )}

      {hasReversal && (
        <Alert variant="danger" className="py-2 my-2 small mb-2">
          <i className="fa-solid fa-rotate-left me-2" />
          <strong>Reversal / clawback detected.</strong>{" "}
          One or more sub-claims have negative amounts. No invoice or claim
          records have been updated — finance must review and apply the
          adjustment manually.
        </Alert>
      )}

      {/* Date-group outcomes — one row per sub-claim, regardless of status. */}
      {dateGroups.length > 0 && (
        <div className="mt-2 mb-2">
          <div className="text-primary fw-semibold small mb-1">Outcomes</div>
          <Table size="sm" bordered className="mb-0 small">
            <thead className="table-light text-center">
              <tr>
                <th style={{ width: "12%" }} className="text-primary fw-semibold">
                  Date
                </th>
                <th style={{ width: "14%" }} className="text-primary fw-semibold">
                  Status
                </th>
                <th style={{ width: "10%" }} className="text-primary fw-semibold text-end">
                  Claimed
                </th>
                <th style={{ width: "10%" }} className="text-primary fw-semibold text-end">
                  Paid
                </th>
                <th style={{ width: "10%" }} className="text-primary fw-semibold text-end">
                  To Patient
                </th>
                <th className="text-primary fw-semibold">Reason / Match</th>
                <th style={{ width: "14%" }} className="text-primary fw-semibold text-center">
                  Action
                </th>
              </tr>
            </thead>
            <tbody>
              {dateGroups.map((g, gi) => {
                const meta = statusMeta(g.status);
                const sub = g.subtotals || {};
                const m = g.matchedClaim || {};
                return (
                  <tr key={`g-${gi}-${g.date}`}>
                    <td className="text-center">
                      {formatGroupDate(g.date)}
                    </td>
                    <td className="text-center">
                      <Badge bg={meta.bg} pill>
                        <i className={`fa-solid ${meta.icon} me-1`} />
                        {meta.label}
                      </Badge>
                    </td>
                    <td className="text-end">{fmtZAR(sub.claimed || 0)}</td>
                    <td className="text-end">{fmtZAR(sub.paid || 0)}</td>
                    <td className="text-end">{fmtZAR(sub.movedToPatient || 0)}</td>
                    <td>
                      {g.reason && <div className="small">{g.reason}</div>}
                      {m.transactionNumber && (
                        <div className="text-muted small">
                          Claim #{m.transactionNumber}
                          {m.statusAfter ? ` • ${m.statusAfter}` : ""}
                        </div>
                      )}
                    </td>
                    <td className="text-center">
                      {g.status === "unallocated" && (
                        <div className="d-flex gap-1 justify-content-center">
                          <Button
                            size="sm"
                            variant="primary"
                            disabled={busy === g.date}
                            onClick={() => handleAllocate(g)}
                          >
                            Allocate
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-secondary"
                            disabled={busy === g.date}
                            onClick={() => handleDismiss(g)}
                          >
                            Dismiss
                          </Button>
                        </div>
                      )}
                      {g.status !== "unallocated" && (
                        <span className="text-muted small">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        </div>
      )}

      {/* Full line-level table — every original line in the era:Claim is
          shown, with its per-line status badge sourced from the dateGroup
          it belongs to (via the line.key ↔ dateGroup.lineKeys mapping). */}
      {lines.length > 0 ? (
        <Table bordered hover size="sm" responsive className="mb-2 align-middle">
          <thead className="table-light text-center">
            <tr>
              <th className="text-primary fw-semibold" style={{ width: "5%" }}>Line #</th>
              <th className="text-primary fw-semibold" style={{ width: "10%" }}>Date of Service</th>
              <th className="text-primary fw-semibold" style={{ width: "10%" }}>Tariff Code</th>
              <th className="text-primary fw-semibold" style={{ width: "8%" }}>Claimed</th>
              <th className="text-primary fw-semibold" style={{ width: "8%" }}>Paid</th>
              <th className="text-primary fw-semibold" style={{ width: "8%" }}>Patient Portion</th>
              <th className="text-primary fw-semibold" style={{ width: "11%" }}>Outcome</th>
              <th className="text-primary fw-semibold" style={{ width: "40%" }}>Reason Codes</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, i) => {
              const grp = lineStatusByKey.get(String(line.key));
              const meta = statusMeta(grp?.status);
              return (
                <tr key={`l-${i}-${line.eraLineNumber}`}>
                  <td className="text-center">{line.eraLineNumber}</td>
                  <td className="text-center">
                    {line.dateOfService
                      ? new Date(line.dateOfService).toLocaleDateString()
                      : "-"}
                  </td>
                  <td className="text-center">{line.tariffCode || "-"}</td>
                  <td className="text-end">{fmtZAR(line.claimedAmount || 0)}</td>
                  <td className="text-end">{fmtZAR(line.paidAmount || 0)}</td>
                  <td className="text-end">{fmtZAR(line.patientPortion || 0)}</td>
                  <td className="text-center">
                    <Badge bg={meta.bg} pill>
                      <i className={`fa-solid ${meta.icon} me-1`} />
                      {meta.label}
                    </Badge>
                  </td>
                  <td className="text-start">
                    {Array.isArray(line.reasonCodes) && line.reasonCodes.length > 0 ? (
                      line.reasonCodes.map((r, k) => (
                        <div key={k} className="small">
                          <Badge bg="warning" text="dark" className="me-2 fw-semibold">
                            {r.code}
                          </Badge>
                          {r.description}
                        </div>
                      ))
                    ) : (
                      <span className="text-muted">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      ) : (
        <p className="text-muted small mb-0">No lines recorded for this claim.</p>
      )}

      {receiptModalShow && (
        <CreateReceiptModal
          show={receiptModalShow}
          onHide={handleReceiptClosed}
          patient={allocCtx.patient}
          account={allocCtx.account}
        />
      )}
    </div>
  );
}
